import VulSource from "./VulSource";
import VulSourceItemChangeLog from "./VulSourceItemChangeLog";
import VulSourceItemFlags from "./VulSourceItemFlags";
import VulSourceItemFlagStats from "./VulSourceItemFlagStats";
import {AiExtractorType, AiExtractorWebSearchMode, ExternalWebSearchBackend, OpenAiServiceTier} from "./AiExtractor";
import {ResourceOwnerType} from "./ResourceOwnerType";
import {AiAttributeResultType, AiAttributeValueRenderType} from "./AiAttribute";
import {Permission} from "./auth/Permission";
import User from "./User";

export interface ReasoningSummaryReasoningNode {
    type: "reasoning";
    text: string;
}

export interface ReasoningSummaryWebSearchSearchNode {
    type: "websearch";
    action: "search";
    query: string;
    status?: string;
    sources?: string[];
    resultCount?: number;
}

export interface ReasoningSummaryWebSearchOpenPageNode {
    type: "websearch";
    action: "open_page";
    url: string;
    status?: string;
    loc?: number;
    numLines?: number;
    totalLines?: number;
    contentBytes?: number;
}

export interface ReasoningSummaryWebSearchFindNode {
    type: "websearch";
    action: "find_in_page";
    pattern: string;
    url?: string;
    status?: string;
    matchCount?: number;
}

export interface ReasoningSummaryUnknownNode {
    type: "unknown";
    provider?: string;
    kind?: string;
    raw?: unknown;
}

export type ReasoningSummaryNode =
    ReasoningSummaryReasoningNode |
    ReasoningSummaryWebSearchSearchNode |
    ReasoningSummaryWebSearchOpenPageNode |
    ReasoningSummaryWebSearchFindNode |
    ReasoningSummaryUnknownNode;

export interface VulSourceItemAiExtractorConfig {
    name?: string;
    type?: string;
    source?: string;
    model?: string;
    openAiServiceTier?: string;
    openAiReasoningEffort?: string;
    anthropicReasoningEffort?: string;
    grokReasoningEffort?: string;
    webSearchMode?: AiExtractorWebSearchMode;
    externalWebSearchBackend?: ExternalWebSearchBackend;
    systemPrompt?: {
        id?: string;
        name?: string;
    };
    supplementaryPrompt?: {
        id?: string;
        name?: string;
    };
    aiProviderConnection?: {
        id?: string;
        name?: string;
        type?: string;
    };
}

export interface TokenUsage {
    inputTokens?: number;
    outputTokens?: number;
    totalTokens?: number;
    inputCachedTokens?: number;
    inputCacheWriteTokens?: number;
    outputReasoningTokens?: number;
}

export interface AiExtractionPriceBreakdown {
    aiExtractorType?: AiExtractorType;
    model?: string;
    openAiServiceTier?: OpenAiServiceTier;
    inputTokens?: number;
    cachedInputTokens?: number;
    inputCacheWriteTokens?: number;
    billableInputTokens?: number;
    outputTokens?: number;
    nativeWebSearchToolCallCount?: number;
    externalWebSearchToolCallCount?: number;
    inputPricePerMillionTokens?: number;
    cachedInputPricePerMillionTokens?: number;
    cacheWriteInputPricePerMillionTokens?: number;
    outputPricePerMillionTokens?: number;
    nativeWebSearchPricePer1kCalls?: number;
    externalWebSearchBackend?: ExternalWebSearchBackend;
    externalWebSearchPricePer1kCalls?: number;
    inputCost?: number;
    cachedInputCost?: number;
    inputCacheWriteCost?: number;
    outputCost?: number;
    nativeWebSearchCost?: number;
    externalWebSearchCost?: number;
    totalCost?: number;
}

export interface AiExtractionResult {
    id?: string;
    aiExtractorId?: string;
    aiExtractorName?: string;
    extractedAt?: Date;
    aiAttributes?: unknown;
    tokenUsage?: TokenUsage;
    executionTimeMs?: number;
    reasoningSummary?: ReasoningSummaryNode[];
    toolTelemetry?: unknown;
    aiExtractorConfig?: VulSourceItemAiExtractorConfig;
    priceBreakdown?: AiExtractionPriceBreakdown;
    aiProviderCommunicationRunId?: string;
}

export interface AiAttributeValueEntry {
    aiExtractorId?: string;
    aiExtractorName?: string;
    aiExtractorOwner?: string;
    aiExtractorOrganizationName?: string;
    valueRating?: number;
    valueText?: string;
    valueTexts?: string[];
    valueSummary?: string;
    message?: string;
}

export interface AggregatedAiAttributeValue {
    resultType?: AiAttributeResultType;
    valueRenderType?: AiAttributeValueRenderType;
    values?: AiAttributeValueEntry[];
}

class VulSourceItem {
    constructor(
        public id?: string,
        public created?: Date,
        public lastModified?: Date,
        public lastModifiedAttributes?: Date,
        public sourcePublished?: Date,
        public sourceLastUpdate?: Date,
        public changedItem?: boolean, // jen pro zobrazení reportu
        public guid?: any,
        public source?: VulSource,
        public jsonDataRaw?: string,
        public jsonData?: any,
        public attributesRaw?: string,
        public aiAttributes?: Record<string, AggregatedAiAttributeValue>,
        public attributes?: any,
        public affected?: any,
        public aiExtractionResults?: AiExtractionResult[],
        public changeLogs?: VulSourceItemChangeLog[],
        public flags?: VulSourceItemFlags,
        public flagStats?: VulSourceItemFlagStats) {
        //
    }

    public static fromPOJO(data: any) {
        if (!data) return null;

        const entity = new VulSourceItem();

        entity.id = data.id;
        entity.created = data.created;
        entity.lastModified = data.lastModified;
        entity.lastModifiedAttributes = data.lastModifiedAttributes;
        entity.sourcePublished = data.sourcePublished;
        entity.sourceLastUpdate = data.sourceLastUpdate;
        entity.changedItem = data.changedItem;
        entity.guid = data.guid;
        entity.source = VulSource.fromPOJO(data.source);
        entity.jsonDataRaw = data.jsonData ?? null;
        entity.jsonData = data.jsonData ? JSON.parse(data.jsonData) : null;
        entity.attributesRaw = data.attributes ?? null;
        entity.aiAttributes = data.aiAttributes ? JSON.parse(data.aiAttributes) : null;
        entity.attributes = data.attributes ? JSON.parse(data.attributes) : null;
        entity.affected = data.affected;
        entity.aiExtractionResults = data.aiExtractionResults;
        entity.changeLogs = data.changeLogs ? data.changeLogs.map(item => VulSourceItemChangeLog.fromPOJO(item)) : null;
        entity.flags = VulSourceItemFlags.fromPOJO(data.flags);
        entity.flagStats = VulSourceItemFlagStats.fromPOJO(data.flagStats);
        return entity;
    }

    public static findOlderChangeLog(changeLogs: VulSourceItemChangeLog[], changeLog: VulSourceItemChangeLog) {
        let logFound = false;

        for (const log of changeLogs) {
            if (logFound && changeLog.type === log.type) {
                return log;
            }

            if (!logFound && log.id === changeLog.id) {
                logFound = true;
            }
        }
    }

    public findOlderChangeLog(changeLog: VulSourceItemChangeLog) {
        return VulSourceItem.findOlderChangeLog(this.changeLogs, changeLog);
    }

    public isReadable(user: User): boolean {
        return (this.source.owner === ResourceOwnerType.ORGANIZATION && user.hasPermission(Permission.SOURCE__READ_ORG)) || (this.source.owner === ResourceOwnerType.SYSTEM && user.hasPermission(Permission.SOURCE__READ_SEAL));
    }

}

export default VulSourceItem;
